import React from 'react'

import PropTypes from 'prop-types'
import InputMask from 'react-input-mask'
import { v4 as uuid } from 'uuid'

import { InfoHelpText, SuccessHelpText, ErrorHelpText } from 'components/forms/components/HelpText'
import {
  Wrapper,
  RowWrapper,
  IconContainer,
  Label,
  StyledInput
} from 'components/forms/components/styled'
import {
  getMask,
  isMasked,
  parseMask,
  doesMaskExist,
  getMaskConditionalPropType
} from 'components/forms/helpers/mask'
import { onlyNumbers, validateField, fullAlphabetic } from 'helpers/validations'

const INPUT_TYPES = {
  PASSWORD: 'password',
  TEXT: 'text'
}

class Input extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isPasswordVisible: false,
      currentMask:
        this.props.masksArray &&
        (this.props.masksArray.find(item => item.length === this.props.field.value.length) ||
          this.props.masksArray[0])
    }
  }

  componentDidMount() {
    this.messageId = uuid()
  }

  handleChange = selectedOption => {
    if (!this.props.disabled) {
      this.setState({ selectedOption })
      this.props.onChange({ selectedOption })
    }
  }

  //Works around final numbers getting weirdly moved when masked input is getting received.
  maskedOnFocusHandler = e => {
    const { field, form, onFocus, masksArray } = this.props
    if (masksArray.findIndex(item => item.length === field.value.length) !== -1) {
      form.setFieldValue(field.name, '')
      this.setState((prevState, prevProps) => ({
        ...prevState,
        currentMask: prevProps.masksArray[0]
      }))
      onFocus && onFocus(e)
    }
  }

  isValidInput = targetValue => {
    const { isNumeric, digitCountArray, isAddress, isMasked, maxLength } = this.props
    const maxPossibleLength = (isMasked && digitCountArray[digitCountArray.length - 1]) || maxLength

    return (
      (targetValue.length <= maxPossibleLength || !maxPossibleLength) &&
      ((!isNumeric && !isAddress) ||
        (isNumeric && validateField(onlyNumbers, targetValue)) ||
        (isAddress && validateField(fullAlphabetic, targetValue)) ||
        targetValue === '')
    )
  }

  beforeMaskedValueChange = (newState, oldState, userInput) => {
    const { digitCountArray, maskChar, maskInterleaveChar, masksArray } = this.props
    const maxPossibleLength = digitCountArray[digitCountArray.length - 1]
    const parsedValue = parseMask(newState.value, maskChar, maskInterleaveChar)
    const oldParsedValue = parseMask(oldState.value, maskChar, maskInterleaveChar)
    const newLength = parsedValue.length
    const currentMaskLength = parseMask(this.state.currentMask, maskChar, maskInterleaveChar).length

    //Voids mask change when masked value is an initial one
    if (!userInput && isMasked(this.props.field.value, this.state.currentMask)) {
      return { selection: newState.selection, value: newState.value }
    }

    if (!userInput && doesMaskExist(newLength, digitCountArray)) {
      const mask = getMask(parsedValue, digitCountArray, masksArray)

      if (mask !== this.state.currentMask) {
        this.setState({ currentMask: mask })
      }
    } else if (
      userInput &&
      (oldParsedValue + userInput).length <= maxPossibleLength &&
      (oldParsedValue + userInput).length > currentMaskLength
    ) {
      const mask = getMask(parsedValue + userInput, digitCountArray, masksArray)
      if (mask !== this.state.currentMask) {
        this.setState({ currentMask: mask })
        return {
          selection: {
            start: newState.selection.start + 1,
            end: newState.selection.end + 1
          },
          value: parsedValue + userInput
        }
      }
    }
    return { selection: newState.selection, value: newState.value }
  }

  getInputComponent = ({
    maskChar,
    maskInterleaveChar,
    isMasked,
    alwaysShowMask,
    field,
    form,
    ariaInvalid,
    ariaDescribedBy,
    ...otherProps
  }) =>
    isMasked ? (
      <InputMask
        {...field}
        {...otherProps}
        beforeMaskedValueChange={this.beforeMaskedValueChange}
        onFocus={this.maskedOnFocusHandler}
        onChange={e => {
          this.props.onChange && this.props.onChange(e)
          const parsedValue = parseMask(e.target.value, maskChar, maskInterleaveChar)
          this.isValidInput(parsedValue) && form.setFieldValue(field.name, parsedValue)
        }}
        alwaysShowMask={alwaysShowMask}
        maskChar={maskChar}
        mask={this.state.currentMask}
      >
        {inputProps => <StyledInput {...inputProps} />}
      </InputMask>
    ) : (
      <>
        <StyledInput
          {...field}
          {...otherProps}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          onChange={e => {
            this.props.onChange && this.props.onChange(e)
            this.isValidInput(e.target.value) && field.onChange(e)
          }}
        />
      </>
    )

  render() {
    const {
      field,
      label,
      e2eClass,
      type,
      isHelpTextVisible = true,
      helperInfo,
      error,
      success,
      infoIcon,
      preventOnPaste,
      ...rest
    } = this.props

    const { isPasswordVisible } = this.state

    return (
      <Wrapper e2eClass={e2eClass}>
        {label && <Label e2eClass={e2eClass}>{label}</Label>}
        <RowWrapper>
          {this.getInputComponent({
            ...rest,
            field,
            label,
            e2eClass,
            type: type === INPUT_TYPES.PASSWORD && isPasswordVisible ? INPUT_TYPES.TEXT : type,
            success,
            error,
            ariaInvalid: !!error,
            ariaDescribedBy: error ? this.messageId : undefined,
            onPaste: e => preventOnPaste && e.preventDefault()
          })}
          {infoIcon && <IconContainer>{infoIcon}</IconContainer>}
        </RowWrapper>
        {isHelpTextVisible && helperInfo && (
          <InfoHelpText e2eClass={e2eClass} message={helperInfo} />
        )}
        {isHelpTextVisible && error && (
          <ErrorHelpText e2eClass={e2eClass} message={error} id={this.messageId} />
        )}
        {isHelpTextVisible && success && (
          <SuccessHelpText e2eClass={e2eClass} message={success} id={this.messageId} />
        )}
      </Wrapper>
    )
  }
}

Input.propTypes = {
  field: PropTypes.shape({
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired
  }),
  label: PropTypes.string,
  type: PropTypes.string,
  isHelpTextVisible: PropTypes.bool,
  preventOnPaste: PropTypes.bool,
  success: PropTypes.string,
  error: PropTypes.string,
  e2eClass: PropTypes.string,
  infoIcon: PropTypes.any,
  maxLength: PropTypes.number,
  isMasked: PropTypes.bool,
  maskInterleaveChar: PropTypes.string,
  maskChar: (props, propName, componentName) =>
    getMaskConditionalPropType(props, propName, componentName, { maskChar: PropTypes.string }),
  digitCountArray: (props, propName, componentName) =>
    getMaskConditionalPropType(props, propName, componentName, {
      digitCountArray: PropTypes.arrayOf(PropTypes.number)
    }),
  masksArray: (props, propName, componentName) =>
    getMaskConditionalPropType(props, propName, componentName, {
      masksArray: PropTypes.arrayOf(PropTypes.string)
    })
}

export default Input
